import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { debounce } from 'lodash-es';
import {
  Accordion,
  AccordionItem,
  Button,
  DatePicker,
  DatePickerInput,
  Dropdown,
  NumberInput,
  Checkbox,
} from '@carbon/react';
import {
  FilterReset,
  OpenPanelLeft,
  OpenPanelFilledLeft,
} from '@carbon/react/icons';
import { atom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { MapRef } from 'react-map-gl';
import Map from 'components/map/map';
import { PolygonArea } from 'components/map';
import { buildPolygonStyle } from 'lib/map';
import { getMapDefaultConfig } from 'components/map/atoms';
import { DebouncedInput } from 'components/debounced-input';
import { DataTable } from 'components/data-table';
import { Pagination } from 'components/pagination';
import {
  itemRangeText,
  pageRangeText,
} from 'components/search/common/format-helpers';
import {
  DEFAULT_FILTER_STATE,
  type DcaScenariosFilterState,
  type DcaScenarioRow,
} from './types';
import { dcaScenariosColumns } from './columns';
import { useDcaScenariosList } from './use-dca-scenarios-list';
import { useDcaScenarioAreas } from './use-dca-scenario-areas';
import styles from './dca-scenarios-list.module.scss';

const PAGE_SIZES = [25, 50, 100];

const mapConfigAtom = atomWithStorage(
  'insights-dca-scenarios-list-map-config',
  getMapDefaultConfig()
);

const dcaListMapRefAtom = atom<MapRef | null>(null);

const DCA_AREA_POLYGON_STYLE = buildPolygonStyle({
  color: '#FFAFD2',
  lineColor: '#D487E1',
  fillOpacity: 0.3,
  lineWidth: 2,
});

const DEVELOPMENT_TYPE_LABELS: Record<string, string> = {
  ALL: 'All',
  HORIZONTAL: 'Horizontal',
  VERTICAL: 'Vertical',
};

/**
 * Builds dropdown options for the Created By filter from API facets.
 * @param {object} facets - The facets object from the API response
 * @return {Array<{id: string, label: string}>} Dropdown options with id and label
 */
function buildCreatedByOptions(facets: {
  createdBy?: { buckets: { value: string; count: number }[] } | null;
}): { id: string; label: string }[] {
  const buckets = facets?.createdBy?.buckets ?? [];
  return buckets.map((b) => ({
    id: b.value,
    label: `${b.value} (${b.count})`,
  }));
}

/**
 * Builds dropdown options for the Development Type filter from API facets.
 * @param {object} facets - The facets object from the API response
 * @return {Array<{id: string, label: string}>} Dropdown options with id and label
 */
function buildDevelopmentTypeOptions(facets: {
  developmentType?: { buckets: { value: string; count: number }[] } | null;
}): { id: string; label: string }[] {
  const buckets = facets?.developmentType?.buckets ?? [];
  return buckets.map((b) => ({
    id: b.value,
    label: `${DEVELOPMENT_TYPE_LABELS[b.value.toUpperCase()] ?? b.value} (${
      b.count
    })`,
  }));
}

function countActiveFilters(f: DcaScenariosFilterState): number {
  let n = 0;
  if (f.createdDateStart || f.createdDateEnd) n++;
  if (f.updatedDateStart || f.updatedDateEnd) n++;
  if (f.createdBy) n++;
  if (f.developmentType) n++;
  if (f.areaCountMin !== '' || f.areaCountMax !== '') n++;
  if (f.formationCountMin !== '' || f.formationCountMax !== '') n++;
  if (f.hasSegmentation) n++;
  if (f.hasOutlierDetection) n++;
  if (f.hasEarlyTime) n++;
  if (f.hasOilConfigs) n++;
  if (f.hasGasConfigs) n++;
  if (f.hasWaterConfigs) n++;
  return n;
}

export const DcaScenariosListContent: FC = () => {
  const [filters, setFilters] =
    useState<DcaScenariosFilterState>(DEFAULT_FILTER_STATE);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const [localAreaCountMin, setLocalAreaCountMin] = useState('');
  const [localAreaCountMax, setLocalAreaCountMax] = useState('');
  const [localFormationCountMin, setLocalFormationCountMin] = useState('');
  const [localFormationCountMax, setLocalFormationCountMax] = useState('');

  const debouncedSetFilter = useMemo(
    () =>
      debounce((key: keyof DcaScenariosFilterState, value: string) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  const {
    rows,
    isLoading,
    error,
    page,
    pageSize,
    totalCount,
    setPage,
    setPageSize,
    search,
    setSearch,
    setSort,
    facets,
    initialSort,
  } = useDcaScenariosList(filters);

  const selectedIdNum =
    selectedRowId !== null &&
    selectedRowId !== undefined &&
    selectedRowId !== ''
      ? Number(selectedRowId)
      : null;
  const { areas: areaGeometries } = useDcaScenarioAreas(selectedIdNum);

  const filterCount = useMemo(() => countActiveFilters(filters), [filters]);
  const createdByOptions = useMemo(
    () => buildCreatedByOptions(facets ?? {}),
    [facets]
  );
  const developmentTypeOptions = useMemo(
    () => buildDevelopmentTypeOptions(facets ?? {}),
    [facets]
  );
  const mapRef = useAtomValue(dcaListMapRefAtom);

  // trigger a resize map when filter panel is toggled so the map fills the new layout (no white gap)
  useEffect(() => {
    if (!mapRef) return;
    mapRef.getMap()?.resize();
  }, [isFilterOpen, mapRef]);

  const handleResetFilters = useCallback(() => {
    debouncedSetFilter.cancel();
    setLocalAreaCountMin('');
    setLocalAreaCountMax('');
    setLocalFormationCountMin('');
    setLocalFormationCountMax('');
    setFilters(DEFAULT_FILTER_STATE);
  }, [debouncedSetFilter]);

  const handleClearCreatedDate = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      createdDateStart: '',
      createdDateEnd: '',
    }));
  }, []);

  const handleClearUpdatedDate = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      updatedDateStart: '',
      updatedDateEnd: '',
    }));
  }, []);

  const setFilter = useCallback(
    <K extends keyof DcaScenariosFilterState>(
      key: K,
      value: DcaScenariosFilterState[K]
    ) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const columns = useMemo(() => dcaScenariosColumns, []);

  return (
    <div className={styles.pageWrapper}>
      <div
        className={`${styles.pageLayout} ${
          isFilterOpen ? styles.pageLayoutWithSidebar : ''
        }`}
      >
        {isFilterOpen && (
          <div className={styles.leftColumn}>
            <div className={styles.leftRow1}>
              <div className={styles.leftResetSection}>
                <Button
                  kind="ghost"
                  size="md"
                  onClick={handleResetFilters}
                  disabled={filterCount === 0}
                  renderIcon={FilterReset}
                >
                  Reset Filters
                </Button>
              </div>
            </div>
            <div className={styles.leftRow2}>
              <Accordion className={styles.filterAccordion}>
                <AccordionItem title="Date Filters" open>
                  <div className={styles.dateFilterGroup}>
                    <span className={styles.dateFilterLabel}>Created Date</span>
                    <div className={styles.dateFilterRow}>
                      <div className={styles.dateFromToCell}>
                        <span className={styles.dateFromToLabel}>From</span>
                        <DatePicker
                          className={styles.datePicker}
                          datePickerType="single"
                          dateFormat="m/d/Y"
                          value={
                            filters.createdDateStart
                              ? new Date(filters.createdDateStart)
                              : undefined
                          }
                          onChange={([d]) =>
                            setFilter(
                              'createdDateStart',
                              d ? moment(d).format('YYYY-MM-DD') : ''
                            )
                          }
                        >
                          <DatePickerInput
                            id="created-date-start"
                            placeholder="mm/dd/yyyy"
                            labelText=""
                            size="md"
                          />
                        </DatePicker>
                      </div>
                      <div className={styles.dateFromToCell}>
                        <span className={styles.dateFromToLabel}>To</span>
                        <DatePicker
                          className={styles.datePicker}
                          datePickerType="single"
                          dateFormat="m/d/Y"
                          value={
                            filters.createdDateEnd
                              ? new Date(filters.createdDateEnd)
                              : undefined
                          }
                          onChange={([d]) =>
                            setFilter(
                              'createdDateEnd',
                              d ? moment(d).format('YYYY-MM-DD') : ''
                            )
                          }
                        >
                          <DatePickerInput
                            id="created-date-end"
                            placeholder="mm/dd/yyyy"
                            labelText=""
                            size="md"
                          />
                        </DatePicker>
                      </div>
                      <div>
                        <Button
                          kind="tertiary"
                          size="sm"
                          onClick={handleClearCreatedDate}
                          disabled={
                            !filters.createdDateStart && !filters.createdDateEnd
                          }
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className={styles.dateFilterGroup}>
                    <span className={styles.dateFilterLabel}>Updated Date</span>
                    <div className={styles.dateFilterRow}>
                      <div className={styles.dateFromToCell}>
                        <span className={styles.dateFromToLabel}>From</span>
                        <DatePicker
                          className={styles.datePicker}
                          datePickerType="single"
                          dateFormat="m/d/Y"
                          value={
                            filters.updatedDateStart
                              ? new Date(filters.updatedDateStart)
                              : undefined
                          }
                          onChange={([d]) =>
                            setFilter(
                              'updatedDateStart',
                              d ? moment(d).format('YYYY-MM-DD') : ''
                            )
                          }
                        >
                          <DatePickerInput
                            id="updated-date-start"
                            placeholder="mm/dd/yyyy"
                            labelText=""
                            size="md"
                          />
                        </DatePicker>
                      </div>
                      <div className={styles.dateFromToCell}>
                        <span className={styles.dateFromToLabel}>To</span>
                        <DatePicker
                          className={styles.datePicker}
                          datePickerType="single"
                          dateFormat="m/d/Y"
                          value={
                            filters.updatedDateEnd
                              ? new Date(filters.updatedDateEnd)
                              : undefined
                          }
                          onChange={([d]) =>
                            setFilter(
                              'updatedDateEnd',
                              d ? moment(d).format('YYYY-MM-DD') : ''
                            )
                          }
                        >
                          <DatePickerInput
                            id="updated-date-end"
                            placeholder="mm/dd/yyyy"
                            labelText=""
                            size="md"
                          />
                        </DatePicker>
                      </div>
                      <div>
                        <Button
                          kind="tertiary"
                          size="sm"
                          onClick={handleClearUpdatedDate}
                          disabled={
                            !filters.updatedDateStart && !filters.updatedDateEnd
                          }
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </div>
                </AccordionItem>
              </Accordion>
            </div>
            <div className={styles.leftRow3}>
              <Accordion className={styles.filterAccordion}>
                <AccordionItem title="DCA Scenario Details" open>
                  <div
                    className={
                      styles.detailsFilterField +
                      (!filters.createdBy
                        ? ' ' + styles.createdByDropdownPlaceholder
                        : '')
                    }
                  >
                    <label
                      className={styles.detailsFilterLabel}
                      htmlFor="created-by"
                    >
                      Created By
                    </label>
                    <Dropdown
                      id="created-by"
                      titleText=""
                      label="Select user"
                      items={createdByOptions}
                      selectedItem={
                        filters.createdBy
                          ? (createdByOptions.find(
                              (i) => i.id === filters.createdBy
                            ) ?? null)
                          : null
                      }
                      onChange={({ selectedItem }) =>
                        setFilter('createdBy', selectedItem?.id ?? '')
                      }
                    />
                  </div>
                  <div
                    className={
                      styles.detailsFilterField +
                      (!filters.developmentType
                        ? ' ' + styles.developmentTypeDropdownPlaceholder
                        : '')
                    }
                  >
                    <label
                      className={styles.detailsFilterLabel}
                      htmlFor="development-type"
                    >
                      Development Type
                    </label>
                    <Dropdown
                      id="development-type"
                      titleText=""
                      label="Select type"
                      items={developmentTypeOptions}
                      selectedItem={
                        filters.developmentType
                          ? (developmentTypeOptions.find(
                              (i) => i.id === filters.developmentType
                            ) ?? null)
                          : null
                      }
                      onChange={({ selectedItem }) =>
                        setFilter('developmentType', selectedItem?.id ?? '')
                      }
                    />
                  </div>
                  <div className={styles.detailsFilterField}>
                    <span className={styles.detailsFilterLabel}>
                      Area Count
                    </span>
                    <div className={styles.minMaxRow}>
                      <NumberInput
                        id="area-count-min"
                        label=""
                        placeholder="Min"
                        allowEmpty={true}
                        hideSteppers={true}
                        value={
                          localAreaCountMin === ''
                            ? ''
                            : Number(localAreaCountMin) || 0
                        }
                        onChange={(
                          _e: unknown,
                          data: { value: number | string }
                        ) => {
                          const val =
                            data.value === '' ? '' : String(data.value);
                          setLocalAreaCountMin(val);
                          debouncedSetFilter('areaCountMin', val);
                        }}
                      />
                      <NumberInput
                        id="area-count-max"
                        label=""
                        placeholder="Max"
                        allowEmpty={true}
                        hideSteppers={true}
                        value={
                          localAreaCountMax === ''
                            ? ''
                            : Number(localAreaCountMax) || 0
                        }
                        onChange={(
                          _e: unknown,
                          data: { value: number | string }
                        ) => {
                          const val =
                            data.value === '' ? '' : String(data.value);
                          setLocalAreaCountMax(val);
                          debouncedSetFilter('areaCountMax', val);
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles.detailsFilterField}>
                    <span className={styles.detailsFilterLabel}>
                      Formation Count
                    </span>
                    <div className={styles.minMaxRow}>
                      <NumberInput
                        id="formation-count-min"
                        label=""
                        placeholder="Min"
                        allowEmpty={true}
                        hideSteppers={true}
                        value={
                          localFormationCountMin === ''
                            ? ''
                            : Number(localFormationCountMin) || 0
                        }
                        onChange={(
                          _e: unknown,
                          data: { value: number | string }
                        ) => {
                          const val =
                            data.value === '' ? '' : String(data.value);
                          setLocalFormationCountMin(val);
                          debouncedSetFilter('formationCountMin', val);
                        }}
                      />
                      <NumberInput
                        id="formation-count-max"
                        label=""
                        placeholder="Max"
                        allowEmpty={true}
                        hideSteppers={true}
                        value={
                          localFormationCountMax === ''
                            ? ''
                            : Number(localFormationCountMax) || 0
                        }
                        onChange={(
                          _e: unknown,
                          data: { value: number | string }
                        ) => {
                          const val =
                            data.value === '' ? '' : String(data.value);
                          setLocalFormationCountMax(val);
                          debouncedSetFilter('formationCountMax', val);
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles.detailsFilterField}>
                    <span className={styles.detailsFilterLabel}>
                      DCA Scenario w/ Configs
                    </span>
                    <div className={styles.checkboxGroup}>
                      <Checkbox
                        id="has-segmentation"
                        labelText="Segmentation"
                        checked={filters.hasSegmentation}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          setFilter('hasSegmentation', checked)
                        }
                      />
                      <Checkbox
                        id="has-outlier"
                        labelText="Outlier Detection"
                        checked={filters.hasOutlierDetection}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          setFilter('hasOutlierDetection', checked)
                        }
                      />
                      <Checkbox
                        id="has-early-time"
                        labelText="Early Time"
                        checked={filters.hasEarlyTime}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          setFilter('hasEarlyTime', checked)
                        }
                      />
                      <Checkbox
                        id="has-oil"
                        labelText="Oil Configs"
                        checked={filters.hasOilConfigs}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          setFilter('hasOilConfigs', checked)
                        }
                      />
                      <Checkbox
                        id="has-gas"
                        labelText="Gas Configs"
                        checked={filters.hasGasConfigs}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          setFilter('hasGasConfigs', checked)
                        }
                      />
                      <Checkbox
                        id="has-water"
                        labelText="Water Configs"
                        checked={filters.hasWaterConfigs}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          setFilter('hasWaterConfigs', checked)
                        }
                      />
                    </div>
                  </div>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        )}

        <div className={styles.rightColumn}>
          <div className={styles.rightRow1}>
            <div className={styles.actionRow}>
              <Button
                kind="ghost"
                size="md"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                aria-label={isFilterOpen ? 'Hide filters' : 'Show filters'}
                renderIcon={isFilterOpen ? OpenPanelFilledLeft : OpenPanelLeft}
              >
                {filterCount === 0
                  ? isFilterOpen
                    ? 'Hide Filters'
                    : 'Show Filters'
                  : `${filterCount} Filter${
                      filterCount === 1 ? '' : 's'
                    } Applied`}
              </Button>
              <div className={styles.stackSearchArea}>
                <DebouncedInput
                  className={styles.stackSearchField}
                  value={search ?? ''}
                  onChange={(value) => setSearch(String(value).trim())}
                  placeholder="Search DCA Scenario"
                />
              </div>
            </div>
          </div>

          <div className={styles.rightRow2}>
            <div className={styles.mapRow}>
              <div className={styles.mapContainer}>
                <Map
                  className={styles.mapFill}
                  longitude={-98}
                  latitude={31}
                  zoom={4}
                  mapConfigAtom={mapConfigAtom}
                  refAtom={dcaListMapRefAtom}
                  showControls
                  showMapFilters
                >
                  {areaGeometries.map(
                    (area) =>
                      area.geometry && (
                        <PolygonArea
                          key={area.id}
                          id={area.id}
                          geometry={area.geometry}
                          style={DCA_AREA_POLYGON_STYLE}
                        />
                      )
                  )}
                </Map>
              </div>
            </div>
          </div>

          <div className={styles.rightRow3}>
            <div className={styles.tableRow}>
              <div className={styles.tableWrapper}>
                <DataTable<DcaScenarioRow>
                  data={rows}
                  columns={columns}
                  getRowId={(row) => String(row.id)}
                  persistenceKey="dca-scenarios-list"
                  isLoading={isLoading}
                  error={error}
                  initialTableState={{
                    sortColumn: initialSort?.sortColumn,
                    sortDirection: initialSort?.sortDirection,
                    columnPinning: { left: ['name'] },
                  }}
                  onSelectionChange={(s) =>
                    setSelectedRowId(s.selection.toggledNodes[0] ?? null)
                  }
                  onSortChange={(s) => setSort(s.sorting)}
                  config={{
                    hideFooter: true,
                    sorting: true,
                    globalSearch: false,
                    columnFilters: false,
                    columnResizing: true,
                    columnReordering: true,
                    columnPinning: true,
                    columnVisibility: true,
                    export: false,
                  }}
                />
                <Pagination
                  pageSizes={PAGE_SIZES}
                  pageSize={pageSize}
                  page={page}
                  totalItems={totalCount}
                  onChange={({ page: newPage, pageSize: newPageSize }) => {
                    if (newPageSize !== pageSize) {
                      setPageSize(newPageSize);
                    } else {
                      setPage(newPage);
                    }
                  }}
                  itemRangeText={itemRangeText}
                  pageRangeText={pageRangeText}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
